import React, { useState, useEffect, useCallback } from 'react';

interface ImageGalleryProps {
  images: string[];
  fieldName: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, fieldName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
    setIsZoomed(false);
    document.body.style.overflow = 'hidden';
  };

  const closeGallery = () => {
    setIsOpen(false);
    setIsZoomed(false);
    document.body.style.overflow = '';
  };

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goNext, goPrev]);

  return (
    <>
      {/* Thumbnail Grid */}
      <div className="space-y-3">
        <div
          className="relative rounded-xl overflow-hidden h-64 md:h-96 shadow-sm cursor-pointer group"
          onClick={() => openGallery(0)}
        >
          <img
            src={images[0]}
            alt={fieldName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
          <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            Tam Ekran ({images.length} Fotoğraf)
          </div>
        </div>

        <div className="flex space-x-3">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => openGallery(idx)}
              className={`w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all hover:opacity-100 ${
                idx === 0
                  ? 'border-primary-500 shadow-md'
                  : 'border-gray-200 opacity-60 hover:border-primary-300'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-medium truncate max-w-xs">{fieldName}</h3>
              <span className="text-white/60 text-sm">{currentIndex + 1} / {images.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsZoomed(!isZoomed)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title={isZoomed ? 'Uzaklaştır' : 'Yakınlaştır'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isZoomed ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  )}
                </svg>
              </button>
              <button
                onClick={closeGallery}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Kapat (ESC)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Image */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden px-4">
            {/* Prev Button */}
            <button
              onClick={goPrev}
              className="absolute left-4 z-10 p-3 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Image */}
            <div
              className={`transition-transform duration-300 ${isZoomed ? 'cursor-zoom-out scale-150' : 'cursor-zoom-in'}`}
              onClick={() => setIsZoomed(!isZoomed)}
            >
              <img
                src={images[currentIndex]}
                alt={`${fieldName} - ${currentIndex + 1}`}
                className="max-h-[75vh] max-w-full object-contain rounded-lg select-none"
                draggable={false}
              />
            </div>

            {/* Next Button */}
            <button
              onClick={goNext}
              className="absolute right-4 z-10 p-3 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Thumbnail Strip */}
          <div className="px-4 py-3">
            <div className="flex justify-center gap-2 overflow-x-auto">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => { setCurrentIndex(idx); setIsZoomed(false); }}
                  className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                    currentIndex === idx
                      ? 'border-white opacity-100 shadow-lg'
                      : 'border-transparent opacity-40 hover:opacity-70'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
